<template>
	<div class="overflow-x-auto">
		<div v-editable="blok" class="overflow-x-auto">
			<table class="w-full rounded-t-2xl overflow-hidden !mx-0 mb-6 md:mb-10 [&_p]:!mb-0 [&_p]:!leading-tight [&_p]:!pb-0">
				<thead v-if="blok.TableContent?.thead" :class="theadColor">
					<tr scope="col">
						<th
							v-for="thead in theadContent"
							:key="thead.uid"
							class="px-3 py-3 first:px-5 last:px-5 space-y-1"
							:class="[theadRowAlignment, blok.TableHeadingTextTransform, horizontalTextAlign]"
							role="columnheader"
							v-html="thead.text"
						/>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(row, rowIndex) in modifyTableContent" :key="rowIndex">
						<td
							v-for="(cell, cellIndex) in row.body"
							:key="cell.uid"
							:colspan="calculateColspan(rowIndex, cellIndex)"
							:rowspan="cell.rowspan || undefined"
							:class="horizontalTextAlign"
						>
							<div
								class="px-3 py-3 first:px-5 last:px-5 space-y-1"
								:class="blok.TableContentTextTransform"
								v-html="newlineToParagraph(cell.value ?? '')"
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script setup lang="ts">
	import type {TableContentStoryblok} from '@/storyblok/types';
	import type {Compact} from '~/types/utils';
	import {humanToComputerIndexing, computerToHumanIndexing} from '@/utils';

	type ContentType = 'heading' | 'content';
	type RowType = 'firstRow' | 'otherRows';

	type TableHeadingBackground = Compact<
		TableContentStoryblok['TableHeadingBackground']
	>;

	type TableRowAlign = Compact<
		| TableContentStoryblok['TableRowAlignHeading']
		| TableContentStoryblok['TableRowAlignContent']
	>;

	type MergeIndex = {
		rowIndex: number;
		cellIndex: number;
	} | null;

	type Props = {
		blok: TableContentStoryblok;
	};

	const props = defineProps<Props>();

	const paragraphClasses = {
		heading: {
			firstRow: {
				big: 'typo-h3',
				medium: 'typo-h4',
				small: 'typo-h5',
			},
			otherRows: {
				big: 'typo-h3',
				medium: 'typo-h4',
				small: 'typo-h5',
			},
		},
		content: {
			firstRow: {
				big: 'typo-body-xl font-bold',
				medium: 'typo-body-l font-bold',
				small: 'typo-body-s font-bold',
			},
			otherRows: {
				big: 'typo-body-xl',
				medium: 'typo-body-l',
				small: 'typo-body-s',
			},
		},
	} as const satisfies Record<ContentType, Record<RowType, any>>;

	const theadColors = {
		green: '[&_th]:bg-brand-mujglobus-green-accent [&_th]:text-body-inverse',
		orange: '[&_th]:bg-background-important [&_th]:text-body-inverse',
		red: '[&_th]:bg-red-800 [&_th]:text-body-inverse',
		grayLight: '[&_th]:bg-background-secondary',
		grayDark: '[&_th]:bg-background-dark [&_th]:text-body-inverse',
	} as const satisfies Record<TableHeadingBackground, string>;

	const rowAlignments = {
		top: 'align-top',
		middle: 'align-middle',
		bottom: 'align-bottom',
	} as const satisfies Record<TableRowAlign, string>;

	const theadColor = computed(() => theadColors[props.blok.TableHeadingBackground || 'green']);
	const theadRowAlignment = computed(() => rowAlignments[props.blok.TableRowAlignHeading || 'bottom']);

	const theadContent = computed(() =>
		props.blok.TableContent?.thead.map((content) => ({
			text: newlineToParagraph(content.value || '', 'heading'),
			uid: content._uid,
		})),
	);

	const horizontalTextAlign = computed(() => {
		const last = props.blok.TableLastColumnAlign === 'left'
			? 'last:text-left'
			: props.blok.TableLastColumnAlign === 'center'
				? 'last:text-center'
				: props.blok.TableLastColumnAlign === 'right' ? 'last:text-right' : '';

		const first = props.blok.TableFirstColumnAlign === 'left'
			? 'first:text-left'
			: props.blok.TableFirstColumnAlign === 'center'
				? 'first:text-center'
				: props.blok.TableFirstColumnAlign === 'right' ? 'first:text-right' : '';

		const other = props.blok.TableOtherColumnsAlign === 'left'
			? 'text-left'
			: props.blok.TableOtherColumnsAlign === 'center'
				? 'text-center'
				: props.blok.TableOtherColumnsAlign === 'right' ? 'text-right' : '';

		return `${last} ${first} ${other}`;
	});

	const findMergeIndices = (mergeType: 'merge-to-left' | 'merge-to-right' | 'merge-to-upper'): MergeIndex[] => {
		const indices: MergeIndex[] = [];

		if (!props.blok.TableContent?.tbody) { return indices; }

		for (let rowIndex = 0; rowIndex < props.blok.TableContent.tbody.length; rowIndex++) {
			const row = props.blok.TableContent.tbody[rowIndex];
			if (Array.isArray(row.body)) {
				for (let cellIndex = 0; cellIndex < row.body.length; cellIndex++) {
					const cell = row.body[cellIndex];
					if (cell.value && cell.value.includes(mergeType)) {
						indices.push({
							rowIndex,
							cellIndex: mergeType === 'merge-to-left'
								? computerToHumanIndexing(cellIndex)
								: cellIndex,
						});
					}
				}
			}
		}
		return indices;
	};

	const mergeToLeftIndices = computed(() => findMergeIndices('merge-to-left'));
	const mergeToUpperIndices = computed(() => findMergeIndices('merge-to-upper'));

	const modifyTableContent = computed(() => {
		if (!props.blok.TableContent?.tbody) {
			return [];
		}

		let tempContent = props.blok.TableContent.tbody.map((row) => ({...row}));

		const mergeUpperIndicesMap = new Map<number, {startIndex: number; rowspan: number}>();

		mergeToUpperIndices.value.forEach((mergeIndex) => {
			if (mergeIndex) {
				const {rowIndex, cellIndex} = mergeIndex;

				const startIndex = tempContent.findIndex((row, idx) => idx <= rowIndex && row.body[cellIndex]?.value);

				if (startIndex !== -1) {
					mergeUpperIndicesMap.set(cellIndex, {
						startIndex,
						rowspan: rowIndex - startIndex + 1,
					});
				}
			}
		});

		tempContent = tempContent.map((row, rowIndex) => {
			const modifiedRow = {...row};

			modifiedRow.body = modifiedRow.body.map((cell, cellIndex) => {
				const mergeInfo = mergeUpperIndicesMap.get(cellIndex);

				if (mergeInfo) {
					const targetCell = tempContent[mergeInfo.startIndex + mergeInfo.rowspan - 1]?.body[cellIndex];

					if (rowIndex === mergeInfo.startIndex && targetCell) {
						return {
							...cell,
							value: (targetCell.value ?? '').replace('merge-to-upper', '').trim(),
							rowspan: mergeInfo.rowspan,
						};
					} else if (rowIndex > mergeInfo.startIndex && rowIndex <= mergeInfo.startIndex + mergeInfo.rowspan - 1) {
						return null;
					}
				}

				return cell;
			}).filter((cell) => cell !== null);

			return modifiedRow;
		});

		tempContent = tempContent.filter((row) => row.body.length > 0);
		tempContent = tempContent.map((row, rowIndex) => {
			let modifiedRow = {...row};

			mergeToLeftIndices.value.forEach((mergeLeftIndex) => {
				if (rowIndex === mergeLeftIndex?.rowIndex) {
					const colspan = mergeLeftIndex.cellIndex;

					modifiedRow = {
						...modifiedRow,
						body: modifiedRow.body.slice(humanToComputerIndexing(colspan)).map((cell, index) => {
							if (index === 0) {
								return {
									...cell,
									value: cell.value?.replace('merge-to-left', '').trim(),
									colspan,
								};
							}
							return cell;
						}),
					};
				}
			});

			return modifiedRow;
		});

		return tempContent;
	});

	function adjustParagraphClass(type: ContentType, lineNumber: number): string {
		const isFirstLine = lineNumber === 0;
		const classPosition: RowType = isFirstLine ? 'firstRow' : 'otherRows';

		// Změňte logiku pro velikost písma, pokud typ je 'heading', jelikož 'TableHeadingOtherRows' byla odstraněna.
		const sizeType = isFirstLine
			? type === 'heading'
				? props.blok.TableHeadingFirstRow || 'big'
				: props.blok.TableContentFirstRow || 'big'
			: type === 'heading'
				? 'medium'
				: props.blok.TableContentOtherRows || 'small';

		return paragraphClasses[type][classPosition][sizeType];
	}

	function newlineToParagraph(text: string, type: ContentType = 'content'): string {
		const classGenerator = adjustParagraphClass.bind(null, type);
		return text.split('\n').map((chunk, index) =>
			chunk.length ? `<p class="${classGenerator(index)}">${chunk}</p>` : null,
		).join('');
	}

	const calculateColspan = (rowIndex: number, cellIndex: number): number | undefined => {
		const mergeLeft = mergeToLeftIndices.value.find((index) => index?.rowIndex === rowIndex && cellIndex === 0);
		if (mergeLeft) {
			return mergeLeft.cellIndex;
		}

		return undefined;
	};
</script>
